'use client';

// T20 — ClaimMediaWorkspace: orchestrator that replaces the legacy media
// tab, wiring the 4 media hooks and 7 media/ components into the
// 3-column (desktop) / 2-column (tablet) / inline-accordion (mobile)
// workspace layout described in the design doc (Section 6.6).

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton, StatusBanner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { WorkspaceHeader } from './WorkspaceHeader';
import { MediaToolbar } from './MediaToolbar';
import { MediaListColumn } from './MediaListColumn';
import { MediaDetailPanel, type MediaDetailPanelProps } from './MediaDetailPanel';
import { AiSummaryPanel } from './AiSummaryPanel';
import { MediaZoomModal } from './MediaZoomModal';
import { useBreakpoint } from './hooks/useBreakpoint';
import { useClaimMediaSlots } from './hooks/useClaimMediaSlots';
import { useClaimMedia, claimMediaKeys } from './hooks/useClaimMedia';
import { useMediaAiEnrichment } from './hooks/useMediaAiEnrichment';
import type { EnrichmentState } from './hooks/useMediaAiEnrichment';
import type { EnrichmentKind, MediaRecord } from './types';
import { portalRequestContext } from './requestContext';
import type { GPS } from './watermark';

export interface ClaimMediaWorkspaceProps {
  jobId: string;
  mode?: 'staff' | 'portal';
  shareToken?: string;
  // Drive the Selfie-mandatory override below. Optional -- callers that
  // don't have this claim data yet (e.g. the public portal) simply don't
  // get the override; Selfie stays whatever the tenant's base config says.
  intimationDate?: string;
  statusCode?: string;
}

// Selfie is mandatory (overriding the tenant's base media-config, which
// has it as optional) for any claim intimated today or later, and for any
// older claim still sitting in one of these early-workflow statuses --
// once a claim has moved past them, an already-uploaded/skipped Selfie
// slot is left alone rather than retroactively flagged as missing.
const SELFIE_MANDATORY_STATUSES = new Set(['fresh', 'pending_photo_upload', 'pending_documents']);

function isSelfieMandatory(intimationDate?: string, statusCode?: string): boolean {
  if (statusCode && SELFIE_MANDATORY_STATUSES.has(statusCode)) return true;
  if (!intimationDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const intimation = new Date(intimationDate);
  intimation.setHours(0, 0, 0, 0);
  return intimation.getTime() >= today.getTime();
}

export function ClaimMediaWorkspace({
  jobId,
  mode = 'staff',
  shareToken,
  intimationDate,
  statusCode,
}: ClaimMediaWorkspaceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint(containerRef);
  const queryClient = useQueryClient();
  const [gps, setGps] = useState<GPS | null>(null);

  const requestContext = mode === 'portal' && shareToken ? portalRequestContext(shareToken) : undefined;

  // GPS is only needed to stamp camera-captured photos in the portal (see
  // MediaDetailPanel's watermark handling) — staff mode never watermarks,
  // so never prompts for location permission.
  useEffect(() => {
    if (mode !== 'portal' || typeof navigator === 'undefined' || !navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      null,
      { enableHighAccuracy: true, maximumAge: 30_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [mode]);

  const { data: config, isLoading: isLoadingConfig, isError: isConfigError } = useClaimMediaSlots(requestContext);
  const {
    photos,
    videos,
    photosError,
    videosError,
    uploadPhoto,
    uploadVideo,
    deletePhoto,
    deleteVideo,
    replacePhoto,
    replaceVideo,
  } = useClaimMedia(jobId, requestContext);
  const { enrichmentByPhotoId, retryEnrichment } = useMediaAiEnrichment(jobId, requestContext);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'uploaded' | 'pending'>('all');
  // Surfaces a failed upload/replace to the user — the mutations themselves
  // have no onError UI, so before this a rejected upload (400 wrong type,
  // 413 too large, network drop) just silently did nothing.
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Garage/Claim Office users never see AI damage-detection insights
  // (cost/percentage breakdown) - same restriction already applied to
  // portal share-link viewers, extended here via the standard localStorage
  // user_type check.
  const isGarage =
    typeof window !== 'undefined' &&
    ['GARAGE', 'CLAIM_OFFICE'].includes(localStorage.getItem('user_type')?.toUpperCase() ?? '');
  // Claim Office (unlike Garage) is view-only on Photos & Videos — no
  // Upload/Replace/Delete, just view/zoom. Enforced again server-side
  // (require_claim_write_access), this is UX only.
  const isClaimOfficeViewOnly =
    typeof window !== 'undefined' &&
    (localStorage.getItem('user_type')?.toUpperCase() ?? '') === 'CLAIM_OFFICE';

  // Garage (logged in or via this share-link portal) can Delete only while
  // the claim is still in its initial intake/review phase -- same set as
  // claim_status_engine.py's AUTO_MANAGED_STATUS_CODES. Upload/Replace stay
  // available at any status. Enforced again server-side
  // (require_claim_delete_access), this is UX only.
  const GARAGE_DELETE_ALLOWED_STATUSES = new Set([
    'fresh', 'pending_documents', 'pending_photo_upload', 'ready_for_initial_approval',
  ]);
  const isGarageOnly =
    mode === 'portal' ||
    (typeof window !== 'undefined' && (localStorage.getItem('user_type')?.toUpperCase() ?? '') === 'GARAGE');
  const garageDeleteBlocked =
    isGarageOnly && !(statusCode && GARAGE_DELETE_ALLOWED_STATUSES.has(statusCode));
  const [aiVisible, setAiVisible] = useState(mode !== 'portal' && !isGarage);
  const [selectedSlotCode, setSelectedSlotCode] = useState<string | null>(null);
  const [zoomedRecord, setZoomedRecord] = useState<MediaRecord | null>(null);

  const effectivePhotoTypes = useMemo(() => {
    if (!config) return undefined;
    if (!isSelfieMandatory(intimationDate, statusCode)) return config.photoTypes;
    return config.photoTypes.map((type) => (type.code === 'selfie' ? { ...type, required: true } : type));
  }, [config, intimationDate, statusCode]);

  // Ordered (group, then sortOrder) lists of uploaded records, mirroring
  // MediaListColumn's display order -- but intentionally NOT filtered by
  // the list's search/status filter, since zoom navigation should page
  // through everything regardless of what's momentarily typed in the
  // search box. Drives MediaZoomModal's Prev/Next arrows + "X / Y" counter.
  const orderedPhotoRecords = useMemo(() => {
    const result: { record: MediaRecord; slotCode: string }[] = [];
    if (!config || !effectivePhotoTypes) return result;
    const sortedGroups = [...config.groups].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const group of sortedGroups) {
      const typesInGroup = effectivePhotoTypes
        .filter((type) => type.groupCode === group.code)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      for (const type of typesInGroup) {
        const record = photos.find((photo) => photo.photo_type === type.code);
        if (record) result.push({ record, slotCode: type.code });
      }
    }
    return result;
  }, [config, effectivePhotoTypes, photos]);

  const orderedVideoRecords = useMemo(() => {
    const result: { record: MediaRecord; slotCode: string }[] = [];
    if (!config) return result;
    const sortedTypes = [...config.videoTypes].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const type of sortedTypes) {
      const record = videos.find((video) => video.video_type === type.code);
      if (record) result.push({ record, slotCode: type.code });
    }
    return result;
  }, [config, videos]);

  const selectedPhotoType = effectivePhotoTypes?.find((type) => type.code === selectedSlotCode) ?? null;
  const selectedVideoType = selectedPhotoType
    ? null
    : config?.videoTypes.find((type) => type.code === selectedSlotCode) ?? null;
  const slotLabel = selectedPhotoType?.label ?? selectedVideoType?.label ?? '';

  const selectedRecord: MediaRecord | null = !selectedSlotCode
    ? null
    : selectedPhotoType
      ? photos.find((photo) => photo.photo_type === selectedSlotCode) ?? null
      : selectedVideoType
        ? videos.find((video) => video.video_type === selectedSlotCode) ?? null
        : null;

  const enrichment = selectedRecord && selectedPhotoType ? enrichmentByPhotoId[selectedRecord.id] : undefined;

  // useMediaAiEnrichment PATCHes enrichment results to the backend via a raw
  // fetch (not a react-query mutation), so it never invalidates useClaimMedia's
  // photo cache itself — do it here once the enrichment call settles, so
  // WorkspaceHeader/MediaDetailPanel pick up the newly-persisted damage data.
  const invalidatePhotos = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: claimMediaKeys.photos(jobId) });
  }, [queryClient, jobId]);

  // AiSummaryPanel only reads live `enrichmentByPhotoId` (populated by
  // runEnrichments/retryEnrichment THIS session), so a claim opened with
  // photos already enriched in a prior session would show a contradictory
  // all-zero summary next to WorkspaceHeader/MediaDetailPanel's real
  // persisted numbers. Seed a map from persisted `detected_damage` and let
  // live session state (fresher) override it per photo id.
  const persistedEnrichmentByPhotoId = useMemo(() => {
    const seeded: Record<string, EnrichmentState> = {};
    for (const photo of photos) {
      if (photo.detected_damage) {
        seeded[photo.id] = { status: 'success', results: { damage: photo.detected_damage } };
      }
    }
    return seeded;
  }, [photos]);
  const aiSummaryEnrichmentByPhotoId = useMemo(
    () => ({ ...persistedEnrichmentByPhotoId, ...enrichmentByPhotoId }),
    [persistedEnrichmentByPhotoId, enrichmentByPhotoId],
  );

  const uploadErrorMessage = useCallback((err: unknown, kind: 'photo' | 'video'): string => {
    const status = (err as { status?: number })?.status;
    if (status === 413) {
      return kind === 'video'
        ? 'That video is too large to upload. Please upload a shorter clip.'
        : 'That photo is too large to upload. Please try a smaller image.';
    }
    if (status === 400) {
      return `That file could not be read as ${kind === 'video' ? 'a video' : 'an image'}. Please choose a different file.`;
    }
    if (status === 0 || status === undefined) {
      return 'Upload failed — check your connection and try again.';
    }
    return (err as { message?: string })?.message || 'Upload failed. Please try again.';
  }, []);

  const handleUpload = useCallback(async (file: File) => {
    if (!selectedSlotCode) return;
    setUploadError(null);
    try {
      if (selectedPhotoType) {
        // AI damage detection/OCR now runs via a server-side background
        // poller (cr067) triggered by the upload itself — see
        // useClaimMedia's photos query, which polls while any photo is
        // still pending/processing. No client-side enrichment call needed.
        await uploadPhoto({ file, photoType: selectedSlotCode });
      } else if (selectedVideoType) {
        await uploadVideo({ file, videoType: selectedSlotCode });
      }
    } catch (err) {
      setUploadError(uploadErrorMessage(err, selectedVideoType ? 'video' : 'photo'));
    }
  }, [selectedSlotCode, selectedPhotoType, selectedVideoType, uploadPhoto, uploadVideo, uploadErrorMessage]);

  const handleReplace = useCallback(async (file: File) => {
    if (!selectedSlotCode || !selectedRecord) return;
    setUploadError(null);
    try {
      if (selectedPhotoType) {
        // See handleUpload's comment above.
        await replacePhoto({ existingId: selectedRecord.id, file, photoType: selectedSlotCode });
      } else if (selectedVideoType) {
        await replaceVideo({ existingId: selectedRecord.id, file, videoType: selectedSlotCode });
      }
    } catch (err) {
      setUploadError(uploadErrorMessage(err, selectedVideoType ? 'video' : 'photo'));
    }
  }, [selectedSlotCode, selectedRecord, selectedPhotoType, selectedVideoType, replacePhoto, replaceVideo, uploadErrorMessage]);

  const handleDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      if (selectedPhotoType) {
        await deletePhoto(selectedRecord.id);
      } else if (selectedVideoType) {
        await deleteVideo(selectedRecord.id);
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }, [selectedRecord, selectedPhotoType, selectedVideoType, deletePhoto, deleteVideo]);

  const handleRetryEnrichment = useCallback(async (kind: EnrichmentKind) => {
    if (!selectedRecord || !selectedPhotoType) return;
    await retryEnrichment(selectedRecord, kind);
    invalidatePhotos();
  }, [selectedRecord, selectedPhotoType, retryEnrichment, invalidatePhotos]);

  const handleZoom = useCallback(() => {
    if (selectedRecord) setZoomedRecord(selectedRecord);
  }, [selectedRecord]);

  // Navigation while zoomed stays within the same media kind (photos or
  // videos) as whatever's currently open, so Next never jumps from a photo
  // straight into a video mid-sequence. Also re-points selectedSlotCode so
  // the list column / detail panel don't look stale once the modal closes.
  const zoomList = zoomedRecord?.video_type ? orderedVideoRecords : orderedPhotoRecords;
  const zoomIndex = zoomedRecord ? zoomList.findIndex((item) => item.record.id === zoomedRecord.id) : -1;
  function goToZoomIndex(index: number) {
    const item = zoomList[index];
    if (item) {
      setZoomedRecord(item.record);
      setSelectedSlotCode(item.slotCode);
    }
  }

  if (isConfigError) {
    return (
      <div ref={containerRef}>
        <StatusBanner variant="error" title="Failed to load media configuration" message="Please refresh the page and try again." />
      </div>
    );
  }

  if (isLoadingConfig || !config) {
    return (
      <div ref={containerRef} className="flex flex-col gap-4" data-testid="media-workspace-loading">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const detailPanelProps: MediaDetailPanelProps = {
    selected: selectedRecord,
    slotLabel,
    enrichment,
    onZoom: handleZoom,
    onDelete: isClaimOfficeViewOnly || garageDeleteBlocked ? undefined : handleDelete,
    onReplace: isClaimOfficeViewOnly ? undefined : handleReplace,
    onUpload: isClaimOfficeViewOnly ? undefined : handleUpload,
    onRetryEnrichment: handleRetryEnrichment,
    mode,
    slotKind: selectedPhotoType ? 'photo' : selectedVideoType ? 'video' : undefined,
    slotCode: selectedSlotCode ?? undefined,
    gps,
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-4" data-testid="media-workspace">
      <div
        className={cn(
          breakpoint === 'mobile' && 'sticky top-0 z-10 -mx-4 flex flex-col gap-2 bg-neutral-50 px-4 pb-2 dark:bg-neutral-950'
        )}
      >
        <WorkspaceHeader
          photoTypes={effectivePhotoTypes}
          photos={photos}
          videos={videos}
          aiVisible={aiVisible}
          compact={breakpoint === 'mobile'}
        />

        {(photosError || videosError) && (
          <StatusBanner variant="error" message="Some photos or videos failed to load. Try refreshing the page." />
        )}

        {uploadError && (
          <StatusBanner
            variant="error"
            message={uploadError}
            dismissible
            onDismiss={() => setUploadError(null)}
          />
        )}

        {deleteError && (
          <StatusBanner
            variant="error"
            message={deleteError}
            dismissible
            onDismiss={() => setDeleteError(null)}
          />
        )}

        <MediaToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          aiVisible={mode === 'portal' || isGarage ? false : aiVisible}
          onAiVisibleChange={mode === 'portal' || isGarage ? undefined : setAiVisible}
        />
      </div>

      {breakpoint === 'desktop' && (
        <div
          className="grid grid-cols-[320px_1fr_320px] gap-4 min-h-0 h-[calc(100vh-320px)]"
          data-testid="media-workspace-desktop-layout"
        >
          <div className="min-h-0 overflow-y-auto rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <MediaListColumn
              groups={config.groups}
              photoTypes={effectivePhotoTypes}
              videoTypes={config.videoTypes}
              photos={photos}
              videos={videos}
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              selectedId={selectedSlotCode}
              onSelect={setSelectedSlotCode}
            />
          </div>
          <div className="min-h-0 overflow-y-auto rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
            <MediaDetailPanel {...detailPanelProps} />
          </div>
          {mode !== 'portal' && !isGarage && aiVisible && (
            <div className="min-h-0 overflow-y-auto rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <AiSummaryPanel enrichmentByPhotoId={aiSummaryEnrichmentByPhotoId} />
            </div>
          )}
        </div>
      )}

      {breakpoint === 'tablet' && (
        <div
          className="grid grid-cols-[280px_1fr] gap-4 min-h-0 h-[calc(100vh-320px)]"
          data-testid="media-workspace-tablet-layout"
        >
          <div className="min-h-0 overflow-y-auto rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <MediaListColumn
              groups={config.groups}
              photoTypes={effectivePhotoTypes}
              videoTypes={config.videoTypes}
              photos={photos}
              videos={videos}
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              selectedId={selectedSlotCode}
              onSelect={setSelectedSlotCode}
            />
          </div>
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <MediaDetailPanel {...detailPanelProps} />
            </div>
            {mode !== 'portal' && !isGarage && aiVisible && (
              <div className="rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
                <AiSummaryPanel enrichmentByPhotoId={aiSummaryEnrichmentByPhotoId} />
              </div>
            )}
          </div>
        </div>
      )}

      {breakpoint === 'mobile' && (
        <div className="flex flex-col gap-4" data-testid="media-workspace-mobile-layout">
          <MediaListColumn
            groups={config.groups}
            photoTypes={effectivePhotoTypes}
            videoTypes={config.videoTypes}
            photos={photos}
            videos={videos}
            searchQuery={searchQuery}
            statusFilter={statusFilter}
            selectedId={selectedSlotCode}
            onSelect={(slotCode) => setSelectedSlotCode(slotCode === selectedSlotCode ? null : slotCode)}
            renderInlineDetail={() => <MediaDetailPanel {...detailPanelProps} />}
          />
        </div>
      )}

      <MediaZoomModal
        record={zoomedRecord}
        onClose={() => setZoomedRecord(null)}
        onPrev={zoomIndex > 0 ? () => goToZoomIndex(zoomIndex - 1) : undefined}
        onNext={zoomIndex >= 0 && zoomIndex < zoomList.length - 1 ? () => goToZoomIndex(zoomIndex + 1) : undefined}
        counter={zoomIndex >= 0 ? { index: zoomIndex + 1, total: zoomList.length } : undefined}
      />
    </div>
  );
}
